// Sidebar categories: every page (by its list route) sits in one group, so the
// sidebar shows a handful of groups instead of ~40 links. GroupedSider renders
// them; this file holds the grouping, search and open/close rules.

export interface SiderGroupDef {
  key: string;
  label: string;
}

export interface SiderLeaf {
  key: string;
  route: string;
  label: string;
}

export interface SiderGroup extends SiderGroupDef {
  leaves: SiderLeaf[];
}

/** Display order of the groups. */
export const SIDEBAR_GROUPS: SiderGroupDef[] = [
  { key: 'dashboards', label: 'Dashboards' },
  { key: 'players', label: 'Players & Store' },
  { key: 'liveops', label: 'LiveOps' },
  { key: 'pushes', label: 'Pushes & Cricket' },
  { key: 'experiments', label: 'Experiments' },
  { key: 'pvp', label: 'PvP' },
  { key: 'system', label: 'System' },
];

/** A page missing from GROUP_OF_ROUTE lands here, last, rather than vanishing. */
export const OTHER_GROUP: SiderGroupDef = { key: 'other', label: 'Other' };

export const GROUP_OF_ROUTE: Record<string, string> = {
  '/today': 'dashboards',
  '/monetization': 'dashboards',
  '/analytics': 'dashboards',
  '/product-metrics': 'dashboards',
  '/ftue-progression': 'dashboards',
  '/bq-insights': 'dashboards',
  '/ai-analytics': 'dashboards',
  '/risks': 'dashboards',
  '/leaderboard-opens': 'dashboards',

  '/players': 'players',
  '/player-data': 'players',
  '/purchases': 'players',
  '/cohorts': 'players',
  '/inbox': 'players',
  '/products': 'players',
  '/store-sync': 'players',

  '/live-events': 'liveops',
  '/tournaments': 'liveops',
  '/hot-events': 'liveops',
  '/missions': 'liveops',
  '/daily-rewards': 'liveops',
  '/daily-challenge': 'liveops',
  '/daily-deals': 'liveops',
  '/offers': 'liveops',
  '/custom-ads': 'liveops',
  '/branding': 'liveops',

  '/liveops': 'pushes',
  '/match-pushes': 'pushes',
  '/cricket-calendar': 'pushes',
  '/matchday-director': 'pushes',
  '/cm-national-teams': 'pushes',

  '/experiments': 'experiments',
  '/experiments-v2': 'experiments',

  '/pvp-live': 'pvp',
  '/pvp-matches': 'pvp',
  '/pvp-review': 'pvp',
  '/pvp-sync': 'pvp',

  '/config': 'system',
  '/deeplinks': 'system',
  '/webhooks': 'system',
  '/cron-jobs': 'system',
  '/diagnostics': 'system',
  '/admins': 'system',
};

/** The group of a route or of a page under it ("/players/123" → players). */
export function groupForRoute(route: string): string | undefined {
  if (GROUP_OF_ROUTE[route]) return GROUP_OF_ROUTE[route];
  const parts = route.split('/').filter(Boolean);
  for (let n = parts.length - 1; n > 0; n--) {
    const g = GROUP_OF_ROUTE[`/${parts.slice(0, n).join('/')}`];
    if (g) return g;
  }
  return undefined;
}

/** Leaves into groups, groups in SIDEBAR_GROUPS order, leaves in app order; empty groups dropped. */
export function groupMenu(leaves: SiderLeaf[]): SiderGroup[] {
  const defs = [...SIDEBAR_GROUPS, OTHER_GROUP];
  const byKey = new Map(defs.map((d) => [d.key, [] as SiderLeaf[]]));
  for (const l of leaves) byKey.get(GROUP_OF_ROUTE[l.route] ?? OTHER_GROUP.key)!.push(l);
  return defs.map((d) => ({ ...d, leaves: byKey.get(d.key)! })).filter((g) => g.leaves.length);
}

/** Pages whose name (or whose group's name) contains the query; all groups for an empty query. */
export function filterGroups(groups: SiderGroup[], query: string): SiderGroup[] {
  const q = query.trim().toLowerCase();
  if (!q) return groups;
  return groups
    .map((g) =>
      g.label.toLowerCase().includes(q)
        ? g
        : { ...g, leaves: g.leaves.filter((l) => l.label.toLowerCase().includes(q)) }
    )
    .filter((g) => g.leaves.length);
}

/** While searching every shown group is open; otherwise only the one open group. */
export function openKeysFor(groups: SiderGroup[], query: string, openKey?: string): string[] {
  if (query.trim()) return groups.map((g) => g.key);
  return openKey ? [openKey] : [];
}

/** Accordion: from antd's new open list, keep only the group just opened (none if it was closed). */
export function nextOpenKey(previous: string[], next: string[]): string | undefined {
  return next.find((k) => !previous.includes(k));
}

/**
 * What the access check depends on: the pages, not the array. Refine's useMenu
 * hands back a new array on every render, so keying the check on the array
 * made it rerun, set state and render again without end.
 */
export function menuSignature(items: { key?: string; route?: string; name: string }[]): string {
  return items.map((m) => `${m.key ?? m.route ?? m.name}|${m.name}`).join(',');
}
